import type {
  WebhookDelivery,
  WebhookEndpoint,
  WebhookOperation,
} from '@compass/client/types/webhook';
import { GenericTable } from '@components/GenericTable';
import { AlertCircle, Inbox, Loader2, RotateCw, X } from 'lucide-react';
import React, { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { FormattedDateTime } from '@/components/FormattedDateTime';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { SimpleTooltipV2 } from '@/components/ui/tooltip';
import { useDebounce } from '@/hooks/useDebounce';
import type { WebhookPermissions } from '@/hooks/useShouldShowWebhooksPage';
import { useStore } from '@/store';
import type { TableConfig } from '@/types/table';
import { getRelativeTime } from '@/utils/date';

import { useBulkRetryWebhookDeliveries, useWebhookDeliveries } from '../hooks/useWebhookDeliveries';
import {
  buildListDeliveriesRequest,
  DELIVERIES_PAGE_SIZE,
  type DeliveryFilterState,
  EMPTY_VALUE,
  emptyDeliveryFilters,
  eventTypeLabel,
  hasActiveDeliveryFilters,
  isBulkRetryable,
  isDeliveryRangeClosed,
  isEndpointActive,
  isUnavailableError,
  LIVE_POLL_INTERVAL_MS,
  MAX_BULK_RETRY_IDS,
  rememberNextCursor,
  webhookErrorMessage,
} from '../utils';
import { DeliveryDrawer } from './DeliveryDrawer';
import { DeliveryFilters } from './DeliveryFilters';
import { OperationProgress } from './OperationProgress';
import { DeliveryStatusBadge } from './StatusBadges';

interface DeliveriesTabProps {
  organizationId: string;
  permissions: WebhookPermissions;
  endpoints: WebhookEndpoint[];
  /** Endpoint detail page: deliveries of this endpoint only, no endpoint filter. */
  lockedEndpointId?: string;
  /** Deep link `?endpoint=`: start filtered to this endpoint. */
  initialEndpointId?: string;
  /** Deep link `?delivery=`: open this delivery's drawer on load. */
  initialDeliveryId?: string;
  /** Called when the deep-linked drawer closes, so the URL can drop `?delivery=`. */
  onDeepLinkClosed?: () => void;
}

/**
 * The delivery log: filters, cursor-paged table with selection, a bulk bar
 * (Retry now by ids), and the delivery drawer. To retry a set of
 * retrying/exhausted deliveries, filter to them (case, endpoint, status, type,
 * time range), select and Retry now.
 *
 * Live re-reads the first page (and an open delivery) every few seconds, like
 * a log tail. It turns off when you page past the first page or set a time
 * range that has already ended (switching it on clears such a range), and pauses while rows are selected so the
 * selection does not shift under you.
 *
 * Deliveries live in the dispatcher. When it is down the list fails with 503
 * and this tab shows an inline error — it never throws, and the Endpoints tab
 * is unaffected.
 */
export function DeliveriesTab({
  organizationId,
  permissions,
  endpoints,
  lockedEndpointId,
  initialEndpointId,
  initialDeliveryId,
  onDeepLinkClosed,
}: DeliveriesTabProps) {
  const { getTimeZone } = useStore();
  const timeZone = getTimeZone();

  const defaultFilters = useMemo(
    () => emptyDeliveryFilters(lockedEndpointId ?? initialEndpointId),
    [lockedEndpointId, initialEndpointId],
  );
  const [filters, setFilters] = useState<DeliveryFilterState>(defaultFilters);
  const [pageSize, setPageSize] = useState(DELIVERIES_PAGE_SIZE);
  const [page, setPage] = useState(1);
  const [cursors, setCursors] = useState<Record<number, string | undefined>>({});
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [openDeliveryId, setOpenDeliveryId] = useState<string | null>(initialDeliveryId ?? null);
  const [bulkOperation, setBulkOperation] = useState<WebhookOperation | null>(null);
  const [live, setLive] = useState(false);
  const liveSwitchId = useId();

  // A later deep link to another delivery (e.g. "View delivery" on a second
  // test event) opens its drawer without remounting, so filters survive.
  useEffect(() => {
    if (initialDeliveryId) setOpenDeliveryId(initialDeliveryId);
  }, [initialDeliveryId]);

  // Typing in the search box should not fire a request per keystroke.
  const debouncedSearch = useDebounce(filters.searchValue, 400);
  const effectiveFilters = useMemo(
    () => ({ ...filters, searchValue: debouncedSearch }),
    [filters, debouncedSearch],
  );

  const request = buildListDeliveriesRequest(
    organizationId,
    effectiveFilters,
    timeZone,
    cursors[page],
    pageSize,
  );
  const livePaused = live && selectedIds.size > 0;
  const query = useWebhookDeliveries(
    request,
    permissions.canListDeliveries,
    live && !livePaused ? LIVE_POLL_INTERVAL_MS : false,
  );
  const deliveries = useMemo(() => query.data?.deliveries ?? [], [query.data?.deliveries]);
  // While a new page loads, the previous page stays on screen as placeholder
  // data; its next_cursor belongs to that page, so paging forward from it would
  // skip or repeat a page. Only a settled page may advance the cursor stack.
  const nextCursor = query.isPlaceholderData ? undefined : query.data?.next_cursor;

  const bulkRetry = useBulkRetryWebhookDeliveries(organizationId);

  const endpointsById = useMemo(() => {
    const map = new Map<string, WebhookEndpoint>();
    endpoints.forEach(e => e.id && map.set(e.id, e));
    return map;
  }, [endpoints]);

  const resetPaging = () => {
    setPage(1);
    setCursors({});
    setSelectedIds(new Set());
  };

  const handleFiltersChange = (next: DeliveryFilterState) => {
    setFilters(next);
    resetPaging();
    // A range that has already ended gets nothing new: Live has nothing to follow.
    if (isDeliveryRangeClosed(next.dateTo, timeZone)) setLive(false);
  };

  // Live follows the newest deliveries: back to the first page, and drop a
  // time range that has already ended. The whole range goes, not just its
  // end: the picker cannot show an open end and would keep displaying one.
  const handleLiveChange = (on: boolean) => {
    setLive(on);
    if (!on) return;
    const rangeClosed = isDeliveryRangeClosed(filters.dateTo, timeZone);
    if (rangeClosed) setFilters({ ...filters, dateFrom: '', dateTo: '' });
    if (rangeClosed || page !== 1) resetPaging();
  };

  const handlePageChange = useCallback(
    (nextPage: number) => {
      if (nextPage > page) {
        if (!nextCursor) return;
        setCursors(prev => rememberNextCursor(prev, page, nextCursor));
      }
      // Older pages do not change under a live tail; leaving page 1 ends Live.
      if (nextPage !== 1) setLive(false);
      setPage(nextPage);
      setSelectedIds(new Set());
    },
    [page, nextCursor],
  );

  const handleSelectAllCurrentPage = useCallback(
    (selected: boolean) =>
      setSelectedIds(
        selected ? new Set(deliveries.map(d => d.id ?? '').filter(Boolean)) : new Set(),
      ),
    [deliveries],
  );

  const selectedDeliveries = deliveries.filter(d => d.id && selectedIds.has(d.id));
  // The server refuses the whole bulk retry if any delivery's endpoint is paused
  // or disabled, so leave those out (an endpoint we cannot see: let it decide).
  const retryableIds = selectedDeliveries
    .filter(d => {
      if (!isBulkRetryable(d.status)) return false;
      const ep = d.endpoint_id ? endpointsById.get(d.endpoint_id) : undefined;
      return !ep || isEndpointActive(ep);
    })
    .map(d => d.id as string)
    .slice(0, MAX_BULK_RETRY_IDS);

  const handleBulkRetry = () => {
    if (retryableIds.length === 0) return;
    bulkRetry.mutate(
      { organization_id: organizationId, delivery_ids: retryableIds },
      {
        onSuccess: op => {
          setSelectedIds(new Set());
          setBulkOperation(op ?? null);
          toast.success(
            `Retrying ${retryableIds.length} ${retryableIds.length === 1 ? 'delivery' : 'deliveries'}`,
          );
        },
        onError: err => toast.error(webhookErrorMessage(err, 'Could not start the bulk retry')),
      },
    );
  };

  const selectionEnabled = permissions.canBulkRetry;

  const closeDrawer = () => {
    if (openDeliveryId && openDeliveryId === initialDeliveryId) onDeepLinkClosed?.();
    setOpenDeliveryId(null);
  };

  const tableConfig = useMemo((): TableConfig<WebhookDelivery> => {
    const columns: TableConfig<WebhookDelivery>['columns'] = [
      {
        key: 'occurred_at',
        header: 'Event time',
        accessor: d => (
          <button
            type="button"
            className="text-left hover:underline"
            onClick={() => d.id && setOpenDeliveryId(d.id)}
            disabled={!permissions.canGetDelivery}
            data-analytics-id="webhooks:delivery:open"
          >
            <FormattedDateTime value={d.occurred_at} />
          </button>
        ),
      },
      {
        key: 'event_type',
        header: 'Type',
        accessor: d => (
          <SimpleTooltipV2 content={d.event_type ?? ''}>
            <span>{eventTypeLabel(d.event_type)}</span>
          </SimpleTooltipV2>
        ),
      },
      {
        key: 'case_id',
        header: 'Case ID',
        accessor: d => <span className="font-mono text-xs">{d.case_id || EMPTY_VALUE}</span>,
        copyable: true,
        copyValue: d => d.case_id ?? '',
      },
    ];
    if (!lockedEndpointId) {
      columns.push({
        key: 'endpoint',
        header: 'Endpoint',
        accessor: d =>
          (d.endpoint_id && endpointsById.get(d.endpoint_id)?.name) ||
          d.endpoint_name ||
          d.url_host ||
          EMPTY_VALUE,
      });
    }
    columns.push(
      {
        key: 'delivery_status',
        header: 'Status',
        accessor: d => <DeliveryStatusBadge status={d.status} />,
      },
      {
        key: 'attempt_count',
        header: 'Attempts',
        accessor: d => <span className="tabular-nums">{d.attempt_count ?? 0}</span>,
      },
      {
        key: 'last_status_code',
        header: 'Last code',
        accessor: d => <span className="tabular-nums">{d.last_status_code || EMPTY_VALUE}</span>,
      },
      {
        key: 'next_attempt_at',
        header: 'Next attempt',
        accessor: d =>
          d.next_attempt_at ? (
            <SimpleTooltipV2 content={<FormattedDateTime value={d.next_attempt_at} />}>
              <span className="text-sm">{getRelativeTime(d.next_attempt_at, timeZone)}</span>
            </SimpleTooltipV2>
          ) : (
            EMPTY_VALUE
          ),
      },
    );
    return {
      columns,
      getRowKey: d => d.id ?? '',
      emptyState: {
        icon: <Inbox className="h-10 w-10 text-muted-foreground" />,
        title: 'No deliveries',
        description: hasActiveDeliveryFilters(effectiveFilters)
          ? 'No deliveries match these filters.'
          : 'Deliveries appear here once events are sent to your endpoints.',
      },
    };
  }, [lockedEndpointId, endpointsById, permissions.canGetDelivery, timeZone, effectiveFilters]);

  const openDelivery = openDeliveryId ? deliveries.find(d => d.id === openDeliveryId) : undefined;
  const openDeliveryEndpoint = openDelivery?.endpoint_id
    ? endpointsById.get(openDelivery.endpoint_id)
    : undefined;
  // Unknown endpoint (not loaded or deleted): let the server decide.
  const drawerEndpointActive = openDeliveryEndpoint ? isEndpointActive(openDeliveryEndpoint) : true;

  const showReset =
    JSON.stringify(filters) !== JSON.stringify(defaultFilters) && hasActiveDeliveryFilters(filters);

  return (
    <div className="flex flex-col gap-3 min-h-0 flex-1">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <DeliveryFilters
          filters={filters}
          onChange={handleFiltersChange}
          endpointOptions={
            lockedEndpointId
              ? undefined
              : endpoints
                  .filter(e => !!e.id)
                  .map(e => ({
                    value: e.id as string,
                    label: e.name || e.url_host || (e.id as string),
                  }))
          }
          timeZone={timeZone}
          showReset={showReset}
          onReset={() => handleFiltersChange(defaultFilters)}
        />
        <div className="flex items-center gap-2">
          <SimpleTooltipV2
            content={
              livePaused
                ? 'Paused while deliveries are selected'
                : `Refresh the newest deliveries every ${LIVE_POLL_INTERVAL_MS / 1000} seconds`
            }
          >
            <div className="flex h-9 items-center gap-2 rounded-md border px-3">
              <Switch
                id={liveSwitchId}
                checked={live}
                onCheckedChange={handleLiveChange}
                disabled={!permissions.canListDeliveries}
                data-analytics-id="webhooks:deliveries:live"
              />
              <Label htmlFor={liveSwitchId} className="flex cursor-pointer items-center gap-1.5">
                {live && (
                  <span className="relative flex h-2 w-2" aria-hidden>
                    {!livePaused && (
                      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-success opacity-75" />
                    )}
                    <span
                      className={
                        livePaused
                          ? 'relative inline-flex h-2 w-2 rounded-full bg-muted-foreground'
                          : 'relative inline-flex h-2 w-2 rounded-full bg-success'
                      }
                    />
                  </span>
                )}
                {livePaused ? 'Live (paused)' : 'Live'}
              </Label>
            </div>
          </SimpleTooltipV2>
        </div>
      </div>

      {selectionEnabled && selectedIds.size > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-md border bg-muted/40 px-3 py-2">
          <span className="text-sm font-medium">{selectedIds.size} selected</span>
          {permissions.canBulkRetry && (
            <SimpleTooltipV2
              content={
                retryableIds.length === 0
                  ? 'Only retrying or exhausted deliveries of active endpoints can be retried. Resend a succeeded delivery from its details.'
                  : ''
              }
            >
              <Button
                size="sm"
                onClick={handleBulkRetry}
                disabled={retryableIds.length === 0 || bulkRetry.isPending}
                data-analytics-id="webhooks:delivery:bulk-retry"
              >
                {bulkRetry.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <RotateCw className="mr-2 h-4 w-4" />
                )}
                Retry now
                {retryableIds.length > 0 && retryableIds.length !== selectedIds.size
                  ? ` (${retryableIds.length})`
                  : ''}
              </Button>
            </SimpleTooltipV2>
          )}
          <Button size="sm" variant="ghost" onClick={() => setSelectedIds(new Set())}>
            <X className="mr-1 h-4 w-4" />
            Clear
          </Button>
        </div>
      )}

      {bulkOperation && (
        <div className="relative">
          <OperationProgress
            organizationId={organizationId}
            operation={bulkOperation}
            canPoll={permissions.canGetOperation}
          />
          <Button
            size="icon"
            variant="ghost"
            className="absolute right-2 top-2 h-7 w-7"
            onClick={() => setBulkOperation(null)}
            aria-label="Dismiss progress"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}

      {query.isError ? (
        <div className="bg-destructive/10 border border-destructive/50 text-destructive px-4 py-3 rounded-md text-sm">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 mt-0.5 shrink-0" />
            <div className="flex-1">
              <p className="font-medium">
                {isUnavailableError(query.error)
                  ? 'Deliveries are temporarily unavailable'
                  : 'Failed to load deliveries'}
              </p>
              <p className="mt-1">{webhookErrorMessage(query.error, 'Please try again later.')}</p>
            </div>
            <Button size="sm" variant="outline" onClick={() => query.refetch()}>
              Try again
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex-1 min-h-0 flex flex-col">
          <GenericTable
            config={tableConfig}
            data={deliveries}
            loading={query.isLoading}
            currentPage={page}
            hasNextPage={!!nextCursor}
            onPageChange={handlePageChange}
            pageSize={pageSize}
            onPageSizeChange={size => {
              setPageSize(size);
              resetPaging();
            }}
            selectionMode={selectionEnabled}
            selectedIds={selectedIds}
            onSelectionChange={setSelectedIds}
            onSelectAllCurrentPage={handleSelectAllCurrentPage}
          />
        </div>
      )}

      {openDeliveryId && (
        <DeliveryDrawer
          organizationId={organizationId}
          deliveryId={openDeliveryId}
          permissions={permissions}
          endpointActive={drawerEndpointActive}
          live={live}
          onClose={closeDrawer}
        />
      )}
    </div>
  );
}
